import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import type { FieldDef, FieldValue } from "./form-data";
import { placeResultToServiceAddress } from "./address";
import { useFormMapsKey } from "./form-values-context";

/**
 * The single field-input renderer — used by the forms Puck components' `render`
 * (so the editor preview and the live widget show identical inputs). Controlled:
 * the caller supplies value + onChange (bound to the form-values context).
 */

const LABEL =
  "jobsite:text-[11px] jobsite:font-bold jobsite:uppercase jobsite:tracking-[0.08em] jobsite:text-muted-foreground";

function Label({ field }: { field: Pick<FieldDef, "label" | "required"> }) {
  return (
    <div className={LABEL}>
      {field.label}
      {field.required && <span className="jobsite:text-primary"> *</span>}
    </div>
  );
}

/** Multi-photo upload: each picked file is uploaded via `uploadImage`, its
 *  returned URL appended to the value (a string[]). */
function ImageUpload({
  value,
  onChange,
  uploadImage,
}: {
  value: FieldValue;
  onChange: (v: FieldValue) => void;
  uploadImage: (file: File) => Promise<string | null>;
}) {
  const urls = Array.isArray(value) ? (value as string[]) : [];
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onPick = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    const added: string[] = [];
    for (const file of Array.from(files)) {
      const url = await uploadImage(file);
      if (url) added.push(url);
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
    if (added.length) onChange([...urls, ...added]);
  };

  return (
    <div className="jobsite:space-y-2">
      {urls.length > 0 && (
        <div className="jobsite:flex jobsite:flex-wrap jobsite:gap-2">
          {urls.map((url) => (
            <div key={url} className="jobsite:relative jobsite:h-16 jobsite:w-16">
              <img
                src={url}
                alt=""
                className="jobsite:h-16 jobsite:w-16 jobsite:rounded-md jobsite:border jobsite:border-border jobsite:object-cover"
              />
              <button
                type="button"
                onClick={() => onChange(urls.filter((u) => u !== url))}
                aria-label="Remove photo"
                className="jobsite:absolute jobsite:-right-1.5 jobsite:-top-1.5 jobsite:flex jobsite:h-5 jobsite:w-5 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-foreground jobsite:text-background"
              >
                <X className="jobsite:h-3 jobsite:w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="jobsite:flex jobsite:h-20 jobsite:w-full jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:gap-1 jobsite:rounded-md jobsite:border jobsite:border-dashed jobsite:border-input jobsite:text-xs jobsite:text-muted-foreground jobsite:transition-colors jobsite:hover:border-ring jobsite:disabled:opacity-60"
      >
        {busy ? (
          <Loader2 className="jobsite:h-5 jobsite:w-5 jobsite:animate-spin" />
        ) : (
          <ImagePlus className="jobsite:h-5 jobsite:w-5" />
        )}
        {busy ? "Uploading…" : urls.length ? "Add another photo" : "Add a photo"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="jobsite:hidden"
        onChange={(e) => onPick(e.target.files)}
      />
    </div>
  );
}

/** FieldType → HTML input type for the plain-Input branch (default "text"). */
const INPUT_TYPE: Partial<Record<FieldDef["type"], string>> = {
  number: "number",
  date: "date",
  email: "email",
  phone: "tel",
};

/** Display text for an address value: the typed string, or the formatted line of
 *  a picked ServiceAddress. */
function addressText(value: FieldValue): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && !Array.isArray(value) && "formatted" in value) {
    return value.formatted;
  }
  return "";
}

const ADDRESS_INPUT_CLASS = "jobsite:h-12 jobsite:border-2 jobsite:text-base";

/** The address input once the Places library is loaded: the shared styled Input
 *  with a Google Autocomplete attached. Picking a suggestion stores a structured
 *  ServiceAddress; typing stores a plain string (submitted as-is, resolved
 *  server-side). Rendered only inside <APIProvider>, so useMapsLibrary is safe.
 *
 *  The Autocomplete needs the DOM <input>, but Input isn't ref-forwarding on
 *  React 18 — so we read it from a wrapper rather than change a shared component. */
function PlacesAddressInput({
  field,
  value,
  onChange,
  placeholder,
}: {
  field: FieldDef;
  value: FieldValue;
  onChange: (v: FieldValue) => void;
  placeholder: string;
}) {
  const places = useMapsLibrary("places");
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Latest onChange without re-running the attach effect.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const inputEl = wrapperRef.current?.querySelector("input");
    if (!places || !inputEl) return;
    const autocomplete = new places.Autocomplete(inputEl, {
      types: ["address"],
      fields: ["formatted_address", "address_components", "geometry", "place_id"],
      componentRestrictions: { country: "us" },
    });
    const listener = autocomplete.addListener("place_changed", () => {
      const parsed = placeResultToServiceAddress(autocomplete.getPlace());
      if (parsed) onChangeRef.current(parsed);
    });
    return () => listener.remove();
  }, [places]);

  return (
    <div ref={wrapperRef}>
      <Input
        type="text"
        autoComplete={field.autoComplete}
        placeholder={placeholder}
        value={addressText(value)}
        onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
        className={ADDRESS_INPUT_CLASS}
      />
    </div>
  );
}

/** Address field: Places autocomplete when a maps key is available (live
 *  jobsite), a plain text input otherwise (editor preview / no key). Kept on the
 *  classic Autocomplete (not PlaceAutocompleteElement) so the input keeps the
 *  jobsite's own styling and shares web's parse; migrate both to the new API
 *  together later. */
function AddressInput(props: {
  field: FieldDef;
  value: FieldValue;
  onChange: (v: FieldValue) => void;
  placeholder: string;
}) {
  const mapsKey = useFormMapsKey();
  if (!mapsKey) {
    return (
      <Input
        type="text"
        autoComplete={props.field.autoComplete}
        placeholder={props.placeholder}
        value={addressText(props.value)}
        onChange={(e) => props.onChange(e.target.value === "" ? undefined : e.target.value)}
        className={ADDRESS_INPUT_CLASS}
      />
    );
  }
  return (
    <APIProvider apiKey={mapsKey}>
      <PlacesAddressInput {...props} />
    </APIProvider>
  );
}

export function FieldInput({
  field,
  value,
  onChange,
  uploadImage,
}: {
  field: FieldDef;
  value: FieldValue;
  onChange: (v: FieldValue) => void;
  uploadImage?: ((file: File) => Promise<string | null>) | null;
}) {
  const help = field.helpText ? (
    <p className="jobsite:mt-1 jobsite:text-xs jobsite:text-muted-foreground">{field.helpText}</p>
  ) : null;
  // Compact, placeholder-first fields (the original jobsite form look): the
  // label lives in the placeholder, with a screen-reader-only label for a11y.
  const placeholder = `${field.label}${field.required ? " *" : ""}`;

  switch (field.type) {
    case "textarea":
      return (
        <label className="jobsite:block">
          <span className="jobsite:sr-only">{field.label}</span>
          <Textarea
            rows={4}
            placeholder={placeholder}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="jobsite:resize-none jobsite:border-2 jobsite:text-base"
          />
          {help}
        </label>
      );
    case "select":
      return (
        <label className="jobsite:block">
          <span className="jobsite:sr-only">{field.label}</span>
          <select
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value || undefined)}
            className="jobsite:h-12 jobsite:w-full jobsite:rounded-md jobsite:border-2 jobsite:border-input jobsite:bg-transparent jobsite:px-3 jobsite:text-base jobsite:text-foreground jobsite:outline-none jobsite:focus-visible:border-ring jobsite:focus-visible:ring-ring/50 jobsite:focus-visible:ring-[3px]"
          >
            {/* The label rides the empty option, like a placeholder. */}
            <option value="">{placeholder}</option>
            {(field.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {help}
        </label>
      );
    case "multiselect": {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      const toggle = (v: string) =>
        onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
      return (
        <div className="jobsite:space-y-1.5">
          <Label field={field} />
          <div className="jobsite:space-y-1.5">
            {(field.options ?? []).map((o) => (
              <label key={o.value} className="jobsite:flex jobsite:items-center jobsite:gap-2.5">
                <input
                  type="checkbox"
                  checked={arr.includes(o.value)}
                  onChange={() => toggle(o.value)}
                  className="jobsite:h-[18px] jobsite:w-[18px] jobsite:accent-primary"
                />
                <span className="jobsite:text-sm jobsite:text-foreground">{o.label}</span>
              </label>
            ))}
          </div>
          {help}
        </div>
      );
    }
    case "boolean":
      return (
        <div className="jobsite:space-y-1.5">
          <label className="jobsite:flex jobsite:items-start jobsite:gap-2.5">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              className="jobsite:mt-0.5 jobsite:h-[18px] jobsite:w-[18px] jobsite:shrink-0 jobsite:accent-primary"
            />
            <span className="jobsite:text-sm jobsite:font-medium jobsite:text-foreground">
              {field.label}
              {field.required && <span className="jobsite:text-primary"> *</span>}
            </span>
          </label>
          {help}
        </div>
      );
    case "image":
      // A real uploader on live surfaces; a static placeholder in the editor
      // preview (no upload context there).
      return (
        <div className="jobsite:space-y-1.5">
          <Label field={field} />
          {uploadImage ? (
            <ImageUpload value={value} onChange={onChange} uploadImage={uploadImage} />
          ) : (
            <div className="jobsite:flex jobsite:h-20 jobsite:items-center jobsite:justify-center jobsite:rounded-md jobsite:border jobsite:border-dashed jobsite:border-input jobsite:text-xs jobsite:text-muted-foreground">
              Photo upload
            </div>
          )}
          {help}
        </div>
      );
    case "date":
      // Native date inputs ignore placeholders — this one keeps a visible label.
      return (
        <label className="jobsite:block jobsite:space-y-1.5">
          <Label field={field} />
          <Input
            type="date"
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
            className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
          />
          {help}
        </label>
      );
    case "address":
      return (
        <label className="jobsite:block">
          <span className="jobsite:sr-only">{field.label}</span>
          <AddressInput field={field} value={value} onChange={onChange} placeholder={placeholder} />
          {help}
        </label>
      );
    case "number":
    case "text":
    case "email":
    case "phone":
    default:
      return (
        <label className="jobsite:block">
          <span className="jobsite:sr-only">{field.label}</span>
          <Input
            type={INPUT_TYPE[field.type] ?? "text"}
            inputMode={field.type === "phone" ? "tel" : undefined}
            autoComplete={field.autoComplete}
            placeholder={placeholder}
            value={(value as string | number) ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
            className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
          />
          {help}
        </label>
      );
  }
}
